<script setup lang="ts">
import { computed, ref, watch } from "vue";
import type { CountdownStyleProps } from "./shared";
import { resolveLayoutTokens, speedDuration } from "./shared";

const props = defineProps<CountdownStyleProps>();
const layout = computed(() => resolveLayoutTokens(props.block, props.viewport));
const flipMs = computed(() => speedDuration(props.block.animationSpeed, 600));

interface FlipState {
  current: string;
  previous: string;
  flipping: boolean;
  /** Per-character bump used to force a fresh DOM key on every tick so the
   * CSS animation re-runs even when the digit value coincidentally matches. */
  tick: number;
}

const flipMap = ref<Record<string, FlipState>>({});

watch(
  () => props.segments,
  (segs) => {
    for (const seg of segs) {
      const existing = flipMap.value[seg.key];
      if (!existing) {
        flipMap.value[seg.key] = {
          current: seg.value,
          previous: seg.value,
          flipping: false,
          tick: 0,
        };
        continue;
      }
      if (existing.current !== seg.value) {
        flipMap.value[seg.key] = {
          previous: existing.current,
          current: seg.value,
          flipping: true,
          tick: existing.tick + 1,
        };
        const key = seg.key;
        setTimeout(() => {
          if (flipMap.value[key]) flipMap.value[key].flipping = false;
        }, flipMs.value + 50);
      }
    }
  },
  { immediate: true, deep: true },
);

const containerStyle = computed(
  () =>
    ({
      backgroundColor: props.block.backgroundColor || "#0f1115",
      fontFamily:
        props.block.fontFamily || "'Helvetica Neue', Arial, sans-serif",
      padding: `${layout.value.padding}px`,
      gap: `${layout.value.gap}px`,
      flexDirection: layout.value.stack ? "column" : "row",
      minHeight: layout.value.fullscreen ? "60vh" : undefined,
      "--flip-duration": `${flipMs.value}ms`,
    }) as Record<string, string | number | undefined>,
);

const cardSize = computed(() => {
  const base = props.block.digitFontSize * layout.value.digitScale;
  return {
    width: `${Math.round(base * 1.3)}px`,
    height: `${Math.round(base * 1.7)}px`,
    fontSize: `${Math.round(base * 1.25)}px`,
    radius: `${Math.max(4, props.block.cardRadius ?? 8)}px`,
  };
});

const digitColor = computed(() => props.block.digitColor || "#f9fafb");

interface DigitView {
  ci: number;
  current: string;
  previous: string;
  flipping: boolean;
  /** Stable key for the flap layer: changes every flip so animation restarts. */
  flipKey: string;
}

function viewFor(segKey: string, value: string): DigitView[] {
  const state = flipMap.value[segKey];
  return value.split("").map((char, ci) => {
    const prev = state?.previous?.[ci] ?? char;
    const flipping = (state?.flipping ?? false) && prev !== char;
    return {
      ci,
      current: char,
      previous: prev,
      flipping,
      flipKey: `${state?.tick ?? 0}-${ci}-${char}`,
    };
  });
}
</script>

<template>
  <div class="cd-flip" :style="containerStyle">
    <div v-for="(seg, idx) in segments" :key="seg.key" class="cd-flip__group">
      <div class="cd-flip__cards">
        <div
          v-for="d in viewFor(seg.key, seg.value)"
          :key="d.ci"
          class="cd-flip__card"
          :style="{
            width: cardSize.width,
            height: cardSize.height,
            borderRadius: cardSize.radius,
            color: digitColor,
            fontSize: cardSize.fontSize,
          }"
        >
          <!-- Static top half: TOP of the current digit (always visible). -->
          <div class="cd-flip__half cd-flip__half--top">
            <span class="cd-flip__char">{{ d.current }}</span>
          </div>
          <!-- Static bottom half: BOTTOM of the previous digit during a flip,
               otherwise the bottom of the current digit (steady state). -->
          <div class="cd-flip__half cd-flip__half--bot">
            <span class="cd-flip__char">{{
              d.flipping ? d.previous : d.current
            }}</span>
          </div>

          <!-- Flap top: TOP of previous digit, rotates from 0 → -90deg.
               Sits on top of the static top half, then rotates away. -->
          <div
            v-if="d.flipping"
            :key="`top-${d.flipKey}`"
            class="cd-flip__flap cd-flip__flap--top"
          >
            <span class="cd-flip__char">{{ d.previous }}</span>
          </div>
          <!-- Flap bottom: BOTTOM of current digit, rotates from 90 → 0deg.
               Reveals the new bottom-half mid-animation. -->
          <div
            v-if="d.flipping"
            :key="`bot-${d.flipKey}`"
            class="cd-flip__flap cd-flip__flap--bot"
          >
            <span class="cd-flip__char">{{ d.current }}</span>
          </div>

          <div class="cd-flip__divider" aria-hidden="true" />
        </div>
      </div>
      <div
        class="cd-flip__label"
        :style="{
          fontSize: `${block.labelFontSize}px`,
          color: block.labelColor || '#9ca3af',
        }"
      >
        {{ seg.label }}
      </div>
      <div
        v-if="idx < segments.length - 1 && !layout.stack"
        class="cd-flip__sep"
        aria-hidden="true"
      >
        {{ separator }}
      </div>
    </div>
  </div>
</template>

<style scoped>
.cd-flip {
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 12px;
  perspective: 800px;
}
.cd-flip__group {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  position: relative;
}
.cd-flip__cards {
  display: flex;
  gap: 4px;
}
.cd-flip__card {
  position: relative;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  background: linear-gradient(180deg, #1c1f26 0 50%, #14171c 50% 100%);
  box-shadow:
    0 12px 30px rgba(0, 0, 0, 0.55),
    inset 0 1px 0 rgba(255, 255, 255, 0.08);
  overflow: hidden;
  isolation: isolate;
}

/* Each "half" covers the FULL card. clip-path crops it to its half so the
   digit inside renders at full card height and sits centered on the divider —
   which is the only reliable way to make a single character look split. */
.cd-flip__half,
.cd-flip__flap {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  backface-visibility: hidden;
  -webkit-backface-visibility: hidden;
}
.cd-flip__half--top {
  background: linear-gradient(180deg, #2a2e36 0%, #1c1f26 100%);
  clip-path: inset(0 0 50% 0);
  z-index: 1;
}
.cd-flip__half--bot {
  background: linear-gradient(180deg, #14171c 0%, #0c0e12 100%);
  clip-path: inset(50% 0 0 0);
  z-index: 1;
}

.cd-flip__char {
  display: block;
  line-height: 1;
  font-variant-numeric: tabular-nums;
  /* Slight optical nudge so the digit centers on the visual middle of the
     card rather than the typographic line midpoint. */
  transform: translateY(0.02em);
}

.cd-flip__flap--top {
  background: linear-gradient(180deg, #2a2e36 0%, #1c1f26 100%);
  clip-path: inset(0 0 50% 0);
  transform-origin: center bottom;
  animation: cd-flip-top var(--flip-duration, 600ms)
    cubic-bezier(0.55, 0, 0.55, 1) forwards;
  z-index: 3;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.4);
}
.cd-flip__flap--bot {
  background: linear-gradient(180deg, #14171c 0%, #0c0e12 100%);
  clip-path: inset(50% 0 0 0);
  transform-origin: center top;
  transform: rotateX(90deg);
  animation: cd-flip-bot var(--flip-duration, 600ms)
    cubic-bezier(0.55, 0, 0.55, 1) forwards;
  animation-delay: calc(var(--flip-duration, 600ms) * 0.45);
  z-index: 2;
}
@keyframes cd-flip-top {
  0% {
    transform: rotateX(0deg);
  }
  100% {
    transform: rotateX(-90deg);
  }
}
@keyframes cd-flip-bot {
  0% {
    transform: rotateX(90deg);
  }
  100% {
    transform: rotateX(0deg);
  }
}

.cd-flip__divider {
  position: absolute;
  top: 50%;
  left: 0;
  right: 0;
  height: 2px;
  background: rgba(0, 0, 0, 0.55);
  transform: translateY(-1px);
  z-index: 4;
  pointer-events: none;
}

.cd-flip__label {
  font-weight: 500;
  letter-spacing: 0.18em;
  text-transform: uppercase;
}
.cd-flip__sep {
  position: absolute;
  right: -0.55em;
  top: 32%;
  font-weight: 700;
  font-size: 1.4em;
  color: rgba(255, 255, 255, 0.55);
}
</style>
